import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 思维启发与个人成长 - 竖屏「电影感」风格
 *
 * 视觉特点：宽银幕遮幅、戏剧性光影、大字留白排版、沉浸式氛围
 * 适合：名人名言、深度思考、人生哲理、TED 式观点等需要情绪感染力的内容
 * 尺寸：1080x1920 竖屏
 */

interface CinematicProps {
  quote: string;
  author: string;
  authorTitle: string;
  coreIdea: string;
  actionItems: string[];
  endingLine: string;
  moodColor?: string;
}

const DEFAULT_PROPS: CinematicProps = {
  quote: '大多数人高估了一年能做的事，却低估了十年能做的事。',
  author: '比尔·盖茨',
  authorTitle: 'Bill Gates',
  coreIdea: '长期主义：放弃短期回报的诱惑，把时间投入到真正重要的事情上',
  actionItems: [
    '选择一个值得用十年去做的事',
    '每天进步 1%，一年后就是 37 倍',
    '记录过程，而不仅仅追求结果',
  ],
  endingLine: '你的十年之约，从今天开始。',
  moodColor: '#c084fc',
};

export const MindsetCinematicTemplate: React.FC<Partial<CinematicProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const mood = p.moodColor!;

  // Letterbox bars slow reveal
  const barHeight = interpolate(frame, [0, 30], [0, 140], { extrapolateRight: 'clamp' });

  // Scene 1: Quote (0-180)
  const quoteFade = interpolate(frame, [20, 50], [0, 1], { extrapolateRight: 'clamp' });
  const quoteChars = p.quote.length;
  const quoteVisible = Math.floor(interpolate(frame, [30, 30 + quoteChars * 2.5], [0, quoteChars], { extrapolateRight: 'clamp' }));

  // Author (after quote)
  const authorStart = 30 + quoteChars * 2.5 + 20;
  const authorOpacity = interpolate(frame, [authorStart, authorStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Scene 2: Core idea (authorStart + 60)
  const ideaStart = authorStart + 60;
  const ideaOpacity = interpolate(frame, [ideaStart, ideaStart + 30], [0, 1], { extrapolateRight: 'clamp' });
  const ideaY = spring({ frame: frame - ideaStart, fps, from: 40, to: 0, config: { damping: 22, stiffness: 80 } });

  // Scene 3: Action items
  const actionStart = ideaStart + 80;
  const actionInterval = 45;

  // Scene 4: Ending
  const endStart = actionStart + p.actionItems.length * actionInterval + 30;
  const endOpacity = interpolate(frame, [endStart, endStart + 30], [0, 1], { extrapolateRight: 'clamp' });
  const endScale = spring({
    frame: frame - endStart,
    fps,
    from: 0.95,
    to: 1,
    config: { damping: 20, stiffness: 60, mass: 1.2 },
  });

  // Ambient light pulse
  const lightPulse = interpolate(Math.sin(frame / 40), [-1, 1], [0.03, 0.08]);

  // Slow vertical drift for depth
  const driftY = interpolate(frame, [0, 600], [0, -20]);

  return (
    <AbsoluteFill
      style={{
        background: '#000000',
        fontFamily: '"Noto Serif SC", "PingFang SC", Georgia, serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Ambient mood light */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          width: 800,
          height: 800,
          transform: `translate(-50%, -50%)`,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${mood}${Math.round(lightPulse * 255).toString(16).padStart(2, '0')}, transparent 70%)`,
        }}
      />

      {/* Content area with drift */}
      <div style={{ position: 'absolute', inset: 0, transform: `translateY(${driftY}px)` }}>
        {/* Scene 1: Quote */}
        <div
          style={{
            position: 'absolute',
            top: 280,
            left: 80,
            right: 80,
            opacity: quoteFade,
          }}
        >
          <div
            style={{
              fontSize: 52,
              fontWeight: 400,
              lineHeight: 1.8,
              letterSpacing: '0.03em',
            }}
          >
            {p.quote.substring(0, quoteVisible)}
            {quoteVisible < quoteChars && (
              <span
                style={{
                  display: 'inline-block',
                  width: 3,
                  height: 52,
                  background: mood,
                  marginLeft: 4,
                  verticalAlign: 'middle',
                  opacity: frame % 25 < 13 ? 1 : 0,
                }}
              />
            )}
          </div>
        </div>

        {/* Author */}
        <div
          style={{
            position: 'absolute',
            top: 700,
            left: 80,
            right: 80,
            opacity: authorOpacity,
          }}
        >
          <div
            style={{
              width: 50,
              height: 2,
              background: `${mood}80`,
              marginBottom: 20,
            }}
          />
          <div style={{ fontSize: 38, fontWeight: 600, color: mood }}>{p.author}</div>
          <div style={{ fontSize: 34, color: 'rgba(255,255,255,0.35)', marginTop: 6, fontStyle: 'italic' }}>
            {p.authorTitle}
          </div>
        </div>

        {/* Scene 2: Core idea */}
        <div
          style={{
            position: 'absolute',
            top: 900,
            left: 80,
            right: 80,
            opacity: ideaOpacity,
            transform: `translateY(${ideaY}px)`,
          }}
        >
          <div
            style={{
              padding: '32px 36px',
              borderLeft: `3px solid ${mood}60`,
              background: `rgba(255,255,255,0.03)`,
            }}
          >
            <div style={{ fontSize: 34, color: `${mood}90`, fontWeight: 600, letterSpacing: '0.15em', marginBottom: 12 }}>
              CORE IDEA
            </div>
            <div style={{ fontSize: 38, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)' }}>
              {p.coreIdea}
            </div>
          </div>
        </div>

        {/* Scene 3: Action items */}
        <div
          style={{
            position: 'absolute',
            top: 1230,
            left: 80,
            right: 80,
          }}
        >
          {p.actionItems.map((item, i) => {
            const start = actionStart + i * actionInterval;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 18, stiffness: 100 },
            });
            const opacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  marginBottom: 24,
                  opacity,
                  transform: `translateX(${(1 - progress) * 40}px)`,
                }}
              >
                <div
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: '50%',
                    border: `2px solid ${mood}50`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: 40,
                    color: mood,
                    fontWeight: 700,
                    flexShrink: 0,
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  {i + 1}
                </div>
                <span style={{ fontSize: 38, lineHeight: 1.5, color: 'rgba(255,255,255,0.7)' }}>{item}</span>
              </div>
            );
          })}
        </div>

        {/* Scene 4: Ending line */}
        <div
          style={{
            position: 'absolute',
            bottom: 250,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: endOpacity,
            transform: `scale(${endScale})`,
          }}
        >
          <div
            style={{
              fontSize: 38,
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: mood,
            }}
          >
            {p.endingLine}
          </div>
        </div>
      </div>

      {/* Letterbox bars (cinematic widescreen feel) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: barHeight,
          background: 'black',
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: barHeight,
          background: 'black',
          zIndex: 10,
        }}
      />

      {/* Subtle film grain simulation */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
          opacity: 0.4,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* Progress line on top bar */}
      <div
        style={{
          position: 'absolute',
          top: barHeight - 2,
          left: 0,
          width: `${interpolate(frame, [0, endStart + 60], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 2,
          background: `${mood}60`,
          zIndex: 11,
        }}
      />
    </AbsoluteFill>
  );
};
